import {
  getGetV7PostgresSqlTextQueryKey,
  getV7PostgresSqlText,
} from '@/api-client/postgresql/queries/sql-list-v7-controller/sql-list-v7-controller';
import { getPostgresqlSqlDetailTitle } from '@/database/components/sqlDetail/common/utils';
import { formatTimeRange } from '@/database/utils/format.utils';
import { DATABASE_DETAIL_TYPE, DOCUMENT_TEXT_TYPE } from '@/database/adapter/openDetail.define';
import { DB_TYPE } from '@/common/define/common.define';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import type {
  DetailParams,
  ParameterDetailParams,
  SessionDetailParams,
  SqlDetailParams,
  DocumentTextParams,
} from '../types';
import { formatName, mergeDetailInfo, parseSchema } from './utils';
import { getRequiredMap } from '@/common/utils/core/detailFieldMeta.utils';
import {
  sessionDetailSchema,
  sqlDetailSchema,
  objectDetailSchema,
  parameterDetailSchema,
  documentTextSchema,
  type SessionDetailSchema,
  type PostgresqlSessionDetailAddition,
  type SqlDetailSchema,
  type PostgresqlSqlDetailAddition,
  type ObjectDetailSchema,
  type PostgresqlObjectDetailAddition,
  type ParameterDetailSchema,
  type PostgresqlParameterDetailAddition,
  type DocumentTextSchema,
} from './postgresql.schema';

export const getPostgresqlSessionDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  isOpenedFromPA,
  init,
  instanceId,
}: SessionDetailParams): { name: string; addition: PostgresqlSessionDetailAddition } => {
  const { data } = parseSchema<SessionDetailSchema>(
    sessionDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime, isOpenedFromPA, init }),
    DATABASE_DETAIL_TYPE.SESSION,
  );

  return {
    name: formatName(data.pid),
    addition: {
      dbType: DB_TYPE.POSTGRESQL,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
      pid: data.pid,
      datname: data.datname,
      isOpenedFromPA: data.isOpenedFromPA,
      init: data.init,
    },
  };
};

export const getPostgresqlSqlDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: SqlDetailParams): { name: string; addition: PostgresqlSqlDetailAddition } => {
  const { data } = parseSchema<SqlDetailSchema>(
    sqlDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.SQL,
  );

  return {
    name: getPostgresqlSqlDetailTitle(detailInfo),
    addition: {
      dbType: DB_TYPE.POSTGRESQL,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
      queryId: data.queryId,
      sqlId: data.sqlId,
      userName: data.userName,
      sqlTextHashId: data.sqlTextHashId,
      datname: data.datname,
      dbUserId: data.dbUserId,
    },
  };
};

export const getPostgresqlObjectDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: DetailParams): { name: string; addition: PostgresqlObjectDetailAddition } => {
  const { data } = parseSchema<ObjectDetailSchema>(
    objectDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.OBJECT,
  );

  return {
    name: formatName(data.objectName),
    addition: {
      dbType: DB_TYPE.POSTGRESQL,
      instanceId: data.instanceId,
      objectName: data.objectName,
      databaseName: data.databaseName,
      schemaName: data.schemaName,
      relId: data.relId,
      fromTime: data.fromTime,
      toTime: data.toTime,
      paTrendFromTime: data.paTrendFromTime,
      paTrendToTime: data.paTrendToTime,
    },
  };
};

export const getPostgresqlParameterDetailAddition = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
}: ParameterDetailParams): { name: string; addition: PostgresqlParameterDetailAddition } => {
  const { data } = parseSchema<ParameterDetailSchema>(
    parameterDetailSchema,
    mergeDetailInfo(detailInfo, instanceId, { fromTime, toTime }),
    DATABASE_DETAIL_TYPE.PARAMETER,
  );

  return {
    name: formatName(data.parameterName),
    addition: {
      dbType: DB_TYPE.POSTGRESQL,
      instanceId: data.instanceId,
      fromTime: data.fromTime,
      toTime: data.toTime,
    },
  };
};

export const getPostgresqlDocumentTextParams = ({
  detailInfo,
  fromTime,
  toTime,
  instanceId,
  period,
  useFieldValidation,
}: DocumentTextParams) => {
  const formattedTime = formatTimeRange({ fromTime, toTime });
  const timeInfo = period ? { period } : formattedTime;

  if (!useFieldValidation) {
    return {
      instanceId,
      ...timeInfo,
      sqlId: detailInfo.sqlId,
      sqlTextHashId: detailInfo.sqlTextHashId,
    };
  }

  const { data } = parseSchema<DocumentTextSchema>(
    documentTextSchema,
    mergeDetailInfo(detailInfo, instanceId),
    DOCUMENT_TEXT_TYPE,
  );

  return {
    instanceId: data.instanceId,
    ...timeInfo,
    sqlId: data.sqlId,
    sqlTextHashId: data.sqlTextHashId,
  };
};

export const fetchPostgresqlDocumentText = async ({
  frameName,
  abortController,
  ...rest
}: DocumentTextParams) => {
  const params = getPostgresqlDocumentTextParams({ ...rest, useFieldValidation: true });

  const {
    data: { data = [] },
  } = await getV7PostgresSqlText(
    params,
    { params: { frameName: frameName || FRAME_NAMES.DOCUMENT_WINDOW.FULL_TEXT } },
    abortController?.signal,
  );

  return data[0] || {};
};

export const getPostgresqlDocumentTextQueryKey = (params: DocumentTextParams | null) => {
  if (!params) {
    return getGetV7PostgresSqlTextQueryKey();
  }

  const convertedParams = getPostgresqlDocumentTextParams({ ...params, useFieldValidation: false });

  return getGetV7PostgresSqlTextQueryKey(convertedParams);
};

export const postgresqlSqlDetailRequiredMap = getRequiredMap(sqlDetailSchema);
export const postgresqlSessionDetailRequiredMap = getRequiredMap(sessionDetailSchema);
export const postgresqlObjectDetailRequiredMap = getRequiredMap(objectDetailSchema);
export const postgresqlDocumentTextRequiredMap = getRequiredMap(documentTextSchema);
